/**
 * Lobby — waiting room before game starts.
 *
 * Shows room code, player list, difficulty slider (host-controlled),
 * and start/leave controls.
 */

import { useState } from 'react';

export default function Lobby({ roomCode, players, isHost, difficulty, previewWords, onSetDifficulty, onRerollWords, onStartGame, onLeaveRoom }) {
  const [copied, setCopied] = useState(false);

  function copyCode() {
    navigator.clipboard.writeText(roomCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  const diffLabel =
    difficulty <= 20 ? 'Easy' :
    difficulty <= 40 ? 'Moderate' :
    difficulty <= 60 ? 'Medium' :
    difficulty <= 80 ? 'Hard' :
    'Extreme';

  return (
    <div className="landing">
      <div className="landing-card lobby-card">
        <h1 className="landing-title"><span className="logo-l">L</span><span className="logo-e">e</span><span className="logo-x">x</span><span className="logo-i">i</span><span className="logo-quest">Quest</span></h1>
        <div className="landing-stripe" />

        <div className="lobby-code-row">
          <span className="lobby-code-label">Room Code</span>
          <span className="lobby-code">{roomCode}</span>
          <button className="btn-copy" onClick={copyCode}>
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>

        <div className="lobby-players">
          <h2 className="lobby-players-header">
            Players ({players.length}/8)
          </h2>
          {players.map(p => (
            <div key={p.id} className="lobby-player">
              {p.isHost && <span className="lobby-host-star">{'\u2605'}</span>}
              <span className="lobby-player-name">{p.name}</span>
              {p.isHost && <span className="lobby-host-tag">host</span>}
            </div>
          ))}
        </div>

        {/* Difficulty — host can adjust, others see read-only */}
        <div className="lobby-difficulty">
          <h2 className="lobby-difficulty-header">Difficulty</h2>
          <div className="lobby-difficulty-row">
            <span className="difficulty-picker-easy">Easy</span>
            <input
              type="range"
              className="difficulty-slider difficulty-slider-lg"
              min={1}
              max={100}
              value={difficulty}
              onChange={e => isHost && onSetDifficulty(Number(e.target.value))}
              disabled={!isHost}
            />
            <span className="difficulty-picker-hard">Hard</span>
          </div>
          <div className="lobby-difficulty-info">
            <span className="difficulty-picker-level">{diffLabel} ({difficulty})</span>
          </div>
        </div>

        {/* Word preview — start and target words */}
        {previewWords && (
          <div className="lobby-words">
            <h2 className="lobby-words-header">Words</h2>
            <div className="lobby-words-row">
              <div className="lobby-word-pair">
                <span className="lobby-word-label">Start</span>
                <span className="lobby-word-value lobby-word-start">
                  {(previewWords.initialWord || '').replace(/\b\w/g, c => c.toUpperCase())}
                </span>
              </div>
              <span className="lobby-word-arrow">&rarr;</span>
              <div className="lobby-word-pair">
                <span className="lobby-word-label">Target</span>
                <span className="lobby-word-value lobby-word-target">
                  {(previewWords.targetWord || '').replace(/\b\w/g, c => c.toUpperCase())}
                </span>
              </div>
            </div>
            {isHost && (
              <button className="btn-reroll" onClick={onRerollWords}>
                Reroll Words
              </button>
            )}
          </div>
        )}

        <div className="lobby-actions">
          {isHost && (
            <button
              className="btn-landing btn-start"
              onClick={onStartGame}
              disabled={players.length < 2}
            >
              Start Game
            </button>
          )}
          {!isHost && (
            <p className="lobby-wait-msg">Waiting for host to start...</p>
          )}
          <button className="btn-landing btn-leave" onClick={onLeaveRoom}>
            Leave Room
          </button>
        </div>
      </div>
    </div>
  );
}
